import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import ReactMarkdown from "react-markdown";
import { API } from "@/lib/auth";

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    axios.get(`${API}/blog/${slug}`)
      .then((r) => setPost(r.data))
      .catch((e) => { if (e?.response?.status === 404) setNotFound(true); });
  }, [slug]);

  if (notFound) {
    return (
      <div data-testid="blog-not-found" className="pt-40 pb-24 text-center">
        <div className="overline">404</div>
        <h1 className="font-display text-5xl mt-3 tracking-tight">Post not found.</h1>
        <Link to="/blog" className="btn-pill btn-primary mt-8 inline-flex">← Back to journal</Link>
      </div>
    );
  }
  if (!post) return <div className="pt-40 pb-24 text-center text-[hsl(var(--onyx)/0.5)]">Loading…</div>;

  return (
    <article data-testid={`blog-post-${slug}`} className="pt-32 pb-24">
      <div className="mx-auto max-w-[850px] px-6 sm:px-10">
        <Link to="/blog" className="text-sm text-[hsl(var(--onyx)/0.55)] hover:text-[hsl(var(--onyx))]">← Back to journal</Link>
        <div className="mt-8">
          {post.tags && post.tags.length > 0 && (
            <div className="text-[10px] uppercase tracking-widest text-[hsl(var(--gold))]">
              {post.tags.join(" · ")}
            </div>
          )}
          <h1 className="font-display text-4xl sm:text-6xl mt-4 tracking-tight leading-[1.05] text-balance">
            {post.title}
          </h1>
          <div className="mt-6 flex items-center gap-3 text-sm text-[hsl(var(--onyx)/0.5)]">
            {post.author && <span>{post.author}</span>}
            {post.published_at && (
              <>
                <span>·</span>
                <span>{new Date(post.published_at).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}</span>
              </>
            )}
          </div>
        </div>

        {post.cover_image && (
          <div className="mt-10 rounded-[2rem] overflow-hidden aspect-[16/9]">
            <img src={post.cover_image} alt={post.title} className="w-full h-full object-cover" />
          </div>
        )}

        <div className="mt-10 prose prose-lg max-w-none text-[hsl(var(--onyx)/0.8)] leading-relaxed">
          <ReactMarkdown
            components={{
              h1: ({ ...p }) => <h2 className="font-display text-3xl mt-10 mb-4 tracking-tight" {...p} />,
              h2: ({ ...p }) => <h2 className="font-display text-2xl mt-8 mb-3 tracking-tight" {...p} />,
              h3: ({ ...p }) => <h3 className="font-display text-xl mt-6 mb-2 tracking-tight" {...p} />,
              p: ({ ...p }) => <p className="mb-5 leading-relaxed" {...p} />,
              ul: ({ ...p }) => <ul className="mb-5 pl-5 list-disc space-y-2" {...p} />,
              ol: ({ ...p }) => <ol className="mb-5 pl-5 list-decimal space-y-2" {...p} />,
              a: ({ ...p }) => <a className="text-[hsl(var(--burgundy))] underline hover:no-underline" {...p} />,
              blockquote: ({ ...p }) => <blockquote className="border-l-4 border-[hsl(var(--gold))] pl-5 my-6 italic text-[hsl(var(--onyx)/0.7)]" {...p} />,
              img: ({ ...p }) => <img className="rounded-2xl my-8 w-full" {...p} alt={p.alt || ""} />,
            }}
          >
            {post.body_md}
          </ReactMarkdown>
        </div>
      </div>
    </article>
  );
}
